import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useNavigationGuard } from '../context/NavigationContext'
import { isEmbedded } from '../utils/shopifyBridge'

// Every path App.jsx routes to, minus the ones deliberately unreachable.
// A route missing from here is bounced straight back to where the seller
// already was, which does not look like a guard — it looks like the button is
// dead. /upload shipped that way, and /privacy and /terms had been that way
// since they were added, both linked from Settings.
//
// __tests__/routes asserts this stays in step with App.jsx.
//
// /orders stays listed although the Orders section is hidden for eBay-hub
// shops. Whether a path is routable is not a per-shop question — it is a real
// route for the shopify and two_way shops that still have the section — and
// this guard has no shop context to answer it with anyway. The route itself
// decides, in App.jsx via hooks/useOrdersHidden.
const VALID_PATHS = [
  '/', '/dashboard', '/products', '/orders', '/profiling', '/upload',
  '/activity', '/pricing', '/settings',
  // Kept only so an old /templates link reaches its redirect to /profiling.
  '/templates',
  '/privacy', '/terms',
]

// Sections with a record id underneath them. Matching only the exact section
// path sent every detail route straight back to the list, which looked like
// the link was dead.
const VALID_PREFIXES = ['/products/', '/profiling/']

export function isValidPath(pathname) {
  return VALID_PATHS.includes(pathname) ||
         VALID_PREFIXES.some(prefix => pathname.startsWith(prefix) && pathname.length > prefix.length)
}

export default function RouteGuard({ children }) {
  const location  = useLocation()
  const navigate  = useNavigate()
  const { consume } = useNavigationGuard()
  const approvedPath  = useRef(location.pathname)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false

      // When embedded in Shopify Admin, Shopify owns the URL.
      // Never navigate away on first load — doing so drops the host/shop
      // query params and causes Shopify Admin to show a 404.
      if (isEmbedded()) {
        approvedPath.current = location.pathname
        return
      }

      // Standalone (non-embedded): always start at products
      if (!isValidPath(location.pathname) || location.pathname === '/') {
        navigate('/dashboard', { replace: true })
      } else {
        approvedPath.current = location.pathname
      }
      return
    }

    // Subsequent navigations: allow only if path is valid AND either
    // authorized by sidebar click OR we are embedded (Shopify controls URL).
    // Invalid paths are always redirected — even in embedded mode.
    if (isValidPath(location.pathname) && (isEmbedded() || consume())) {
      approvedPath.current = location.pathname
    } else {
      navigate(approvedPath.current, { replace: true })
    }
  }, [location.pathname]) // eslint-disable-line

  return children
}
